import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { AuthService } from '../services/auth.service';

interface SettingsSection {
  key: 'account' | 'branded-domain' | 'billing' | 'security' | 'team' | 'danger';
  labelKey: string;
  icon: string;
  route: string;
}

@Component({
  selector: 'app-account-settings-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RouterOutlet,
    TranslateModule,
    NzIconModule,
    NzMenuModule,
    NzTabsModule,
    NzCardModule,
  ],
  templateUrl: './account-settings-shell.component.html',
  styleUrl: './account-settings-shell.component.less'
})
export class AccountSettingsShellComponent implements OnInit {
  sections: SettingsSection[] = [
    {
      key: 'account',
      labelKey: 'ACCOUNT_SETTINGS.SECTIONS.ACCOUNT',
      icon: 'idcard',
      route: '/account/settings/account',
    },
    {
      key: 'branded-domain',
      labelKey: 'ACCOUNT_SETTINGS.SECTIONS.BRANDED_DOMAIN',
      icon: 'global',
      route: '/account/settings/branded-domain',
    },
    {
      key: 'billing',
      labelKey: 'ACCOUNT_SETTINGS.SECTIONS.BILLING',
      icon: 'file-text',
      route: '/account/settings/billing',
    },
    {
      key: 'security',
      labelKey: 'ACCOUNT_SETTINGS.SECTIONS.SECURITY',
      icon: 'safety',
      route: '/account/settings/security',
    },
    {
      key: 'team',
      labelKey: 'ACCOUNT_SETTINGS.SECTIONS.TEAM_ACCESS',
      icon: 'team',
      route: '/account/settings/team-access',
    },
    {
      key: 'danger',
      labelKey: 'ACCOUNT_SETTINGS.SECTIONS.DANGER',
      icon: 'warning',
      route: '/account/settings/danger',
    },
  ];

  activeSectionKey: SettingsSection['key'] = 'account';
  isMobile = false;

  constructor(private router: Router, private authService: AuthService) {}

  ngOnInit(): void {
    if (!this.authService.hasAnyPermissions([
      'company.billing.read',
    ])) {
      this.sections = this.sections.filter((section) => section.key !== 'billing');
    }

    if (!this.authService.hasAnyPermissions([
      'company.users.read',
      'company.users.invite',
      'company.users.update',
      'company.users.remove'
    ])) {
      this.sections = this.sections.filter((section) => section.key !== 'team');
    }

    this.updateResponsive();
    this.syncActiveFromUrl(this.router.url);

    this.router.events.subscribe(() => {
      this.syncActiveFromUrl(this.router.url);
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateResponsive();
  }

  navigate(section: SettingsSection): void {
    this.router.navigateByUrl(section.route);
  }

  onMobileTabChange(index: number): void {
    const target = this.sections[index];
    if (target) {
      this.navigate(target);
    }
  }

  get selectedTabIndex(): number {
    return this.sections.findIndex((item) => item.key === this.activeSectionKey);
  }

  private updateResponsive(): void {
    this.isMobile = window.matchMedia('(max-width: 900px)').matches;
  }

  private syncActiveFromUrl(url: string): void {
    if (url.includes('/account/settings/security')) {
      this.activeSectionKey = 'security';
      return;
    }
    if (url.includes('/account/settings/branded-domain')) {
      this.activeSectionKey = 'branded-domain';
      return;
    }
    if (url.includes('/account/settings/billing')) {
      this.activeSectionKey = 'billing';
      return;
    }
    if (url.includes('/account/settings/team-access')) {
      this.activeSectionKey = 'team';
      return;
    }
    if (url.includes('/account/settings/danger')) {
      this.activeSectionKey = 'danger';
      return;
    }
    this.activeSectionKey = 'account';
  }
}
